import { router } from '@inertiajs/react'
import { AlertTriangle } from 'lucide-react'

export interface PendingEquityWarningPayload {
  // Omitted entirely when the organization hides commissions and equity — the
  // banner then falls back to the amount-free agreement reminder.
  pending_count?: number
  pending_amount?: number
  earliest_expires_at?: string | null
  days_remaining?: number | null
  severity: 'critical' | 'warning' | 'notice' | 'agreement_only'
  signing_url: string | null
  resend_url: string
  can_resend: boolean
}

interface Props {
  warning: PendingEquityWarningPayload | null
}

const severityClasses: Record<PendingEquityWarningPayload['severity'], string> = {
  critical: 'border-l-red-500 bg-red-50',
  warning: 'border-l-orange-400 bg-orange-50',
  notice: 'border-l-yellow-400 bg-yellow-50',
  agreement_only: 'border-l-chestnut-gold bg-yellow-50',
}

const iconClasses: Record<PendingEquityWarningPayload['severity'], string> = {
  critical: 'text-red-600',
  warning: 'text-orange-500',
  notice: 'text-yellow-600',
  agreement_only: 'text-chestnut-gold',
}

function formatCredits(val: number) {
  return val.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })
}

export default function PendingEquityWarning({ warning }: Props) {
  if (!warning) return null

  const handleResend = () => {
    router.post(warning.resend_url, {}, { preserveScroll: true })
  }

  const earliest = warning.earliest_expires_at ? new Date(warning.earliest_expires_at) : null
  const isAgreementOnly = warning.severity === 'agreement_only'

  const heading = isAgreementOnly
    ? 'Agreement waiting for signature'
    : 'Pending Equity Credits at Risk'

  return (
    <div className={`card border-l-4 ${severityClasses[warning.severity]}`}>
      <div className="flex items-start gap-3">
        <AlertTriangle className={`h-6 w-6 mt-0.5 flex-shrink-0 ${iconClasses[warning.severity]}`} />
        <div className="flex-1 min-w-0">
          <h2 className="text-lg font-semibold text-chestnut-charcoal font-heading">
            {heading}
          </h2>

          {isAgreementOnly && (
            <p className="text-sm text-chestnut-charcoal/80 mt-1">
              You have an agent agreement waiting for your signature. Sign it now to start earning
              equity credits — any commissions you receive before signing will only count once the
              agreement is on file.
            </p>
          )}

          {!isAgreementOnly && warning.pending_count !== undefined && warning.pending_count > 0
            && warning.pending_amount !== undefined && earliest
            && warning.days_remaining !== null && warning.days_remaining !== undefined && (
            <p className="text-sm text-chestnut-charcoal/80 mt-1">
              <span className="font-semibold">
                {formatCredits(warning.pending_count)} pending credit{warning.pending_count === 1 ? '' : 's'}
              </span>{' '}
              ({formatCredits(warning.pending_amount)} in equity){' '}
              <span className={warning.severity === 'critical' ? 'text-red-700 font-semibold' : 'text-orange-700 font-semibold'}>
                expire in {warning.days_remaining} day{warning.days_remaining === 1 ? '' : 's'} ({earliest.toLocaleDateString()})
              </span>
              . Sign your agreement before then to keep them.
            </p>
          )}

          <div className="mt-4 flex flex-wrap gap-3">
            {warning.signing_url && (
              <a
                href={warning.signing_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-4 py-2 rounded-lg bg-chestnut-forest text-white text-sm font-medium hover:bg-chestnut-forest/90"
              >
                View &amp; Sign Agreement
              </a>
            )}
            {warning.can_resend && (
              <button
                type="button"
                onClick={handleResend}
                className="inline-flex items-center px-4 py-2 rounded-lg border border-chestnut-charcoal/20 text-chestnut-charcoal text-sm font-medium hover:bg-white"
              >
                Email me the agreement again
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
